import React, { useState, useEffect } from "react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { History, Trash2 } from "lucide-react";
import { LIFECYCLE_STRATEGIES, LIFECYCLE_STATUSES, STRATEGY_BY_VALUE, STATUS_BY_VALUE, statusesForStrategy } from "@/lib/tracker";
import { patchTracker, deleteTrackerHistory } from "@/lib/api";
import { toast } from "sonner";

/**
 * Anklickbares Badge das einen Popover öffnet zum Wechsel von:
 *   - Strategie (HOLD / TRADE / HOLD_THEN_SELL)
 *   - Status (vordefiniert oder frei)
 *   - Exit-Datum (nur bei HOLD_THEN_SELL)
 * Mit History-Anzeige + Löschmöglichkeit fehlerhafter Wechsel.
 */
export const LifecycleBadge = ({ unitId, tracker, onChanged, compact = false }) => {
    const strat = STRATEGY_BY_VALUE[tracker?.lifecycle_strategy] || STRATEGY_BY_VALUE.HOLD;
    const status = STATUS_BY_VALUE[tracker?.lifecycle_status];
    const statusLabel = status?.label || (tracker?.lifecycle_status ? tracker.lifecycle_status : "Ruhend");

    return (
        <Popover>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    className={`inline-flex items-center gap-1.5 rounded-full ${strat.bg} ${strat.border} ${strat.text} border px-2 py-0.5 text-[11px] font-medium hover:brightness-95 transition`}
                    data-testid={`lifecycle-badge-${unitId}`}
                    onClick={(e) => e.stopPropagation()}
                >
                    <span className={`w-1.5 h-1.5 rounded-full ${strat.dot}`} />
                    <span className="font-semibold tracking-wide">{strat.label}</span>
                    {!compact && (
                        <>
                            <span className="opacity-50">·</span>
                            <span className="opacity-80">{statusLabel}</span>
                        </>
                    )}
                    {tracker?.exit_target_date && strat.value === "HOLD_THEN_SELL" && (
                        <span className="opacity-60 ml-1 text-[10px]">→ {tracker.exit_target_date}</span>
                    )}
                </button>
            </PopoverTrigger>
            <PopoverContent className="w-96 p-0" align="start" onClick={(e) => e.stopPropagation()}>
                <LifecycleEditor unitId={unitId} tracker={tracker} onChanged={onChanged} />
            </PopoverContent>
        </Popover>
    );
};

const LifecycleEditor = ({ unitId, tracker, onChanged }) => {
    const [strategy, setStrategy] = useState(tracker?.lifecycle_strategy || "HOLD");
    const [status, setStatus] = useState(tracker?.lifecycle_status || "ruhend");
    const [customStatus, setCustomStatus] = useState("");
    const [exitDate, setExitDate] = useState(tracker?.exit_target_date || "");
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);
    const [showHistory, setShowHistory] = useState(false);

    // Strategie wechselt → Status zurücksetzen falls inkompatibel
    useEffect(() => {
        const valid = statusesForStrategy(strategy).map((s) => s.value);
        if (status && !valid.includes(status) && !customStatus) {
            setStatus(valid[0] || "ruhend");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [strategy]);

    const proposals = statusesForStrategy(strategy);
    const isCustom = !LIFECYCLE_STATUSES.find((s) => s.value === status) && status !== "";

    const save = async () => {
        const finalStatus = customStatus.trim() || status;
        if (!finalStatus) { toast.error("Status erforderlich"); return; }
        setBusy(true);
        try {
            await patchTracker(unitId, {
                lifecycle_strategy: strategy,
                lifecycle_status: finalStatus,
                exit_target_date: strategy === "HOLD_THEN_SELL" ? (exitDate || null) : null,
                note: note.trim() || null,
            });
            toast.success("Status aktualisiert");
            onChanged?.();
            // Popover schließt sich automatisch, sobald ChangeEvent außerhalb erfolgt
            // Hier brauchen wir nichts weiter zu tun; State wird beim nächsten Öffnen frisch geladen
        } catch (err) {
            toast.error(err.response?.data?.detail || "Aktualisierung fehlgeschlagen");
        } finally { setBusy(false); }
    };

    const removeHistoryEntry = async (entryId) => {
        if (!window.confirm("Diesen Historien-Eintrag wirklich löschen?")) return;
        try {
            await deleteTrackerHistory(unitId, entryId);
            toast.success("Eintrag gelöscht");
            onChanged?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Löschen fehlgeschlagen");
        }
    };

    return (
        <div className="p-4 space-y-4">
            {/* Strategie */}
            <div>
                <Label className="text-[11px] uppercase tracking-[0.18em] text-gray-500">Strategie</Label>
                <div className="grid grid-cols-3 gap-1.5 mt-1.5">
                    {LIFECYCLE_STRATEGIES.map((s) => (
                        <button
                            type="button"
                            key={s.value}
                            onClick={() => setStrategy(s.value)}
                            className={`text-left px-2.5 py-2 rounded-md border text-xs transition ${strategy === s.value
                                ? `${s.bg} ${s.border} ${s.text} ring-1 ring-offset-1 ring-${s.color}-400`
                                : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"}`}
                            data-testid={`strategy-option-${s.value}`}
                        >
                            <div className="flex items-center gap-1.5">
                                <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
                                <span className="font-semibold">{s.label}</span>
                            </div>
                            <div className="text-[10px] opacity-70 mt-0.5">{s.short}</div>
                        </button>
                    ))}
                </div>
            </div>

            {/* Exit-Datum nur bei HOLD_THEN_SELL */}
            {strategy === "HOLD_THEN_SELL" && (
                <div>
                    <Label className="text-[11px] uppercase tracking-[0.18em] text-gray-500">Exit-Zieldatum (optional)</Label>
                    <Input type="date" value={exitDate} onChange={(e) => setExitDate(e.target.value)} className="h-8 mt-1.5" data-testid="exit-target-date" />
                </div>
            )}

            {/* Status-Auswahl */}
            <div>
                <Label className="text-[11px] uppercase tracking-[0.18em] text-gray-500">Status</Label>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {proposals.map((s) => (
                        <button
                            type="button"
                            key={s.value}
                            onClick={() => { setStatus(s.value); setCustomStatus(""); }}
                            className={`px-2 py-1 rounded-full text-[11px] border transition ${status === s.value && !customStatus
                                ? "bg-slate-900 text-white border-slate-900"
                                : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50"}`}
                            data-testid={`status-option-${s.value}`}
                        >
                            {s.label}
                        </button>
                    ))}
                    {isCustom && !customStatus && (
                        <span className="px-2 py-1 rounded-full text-[11px] bg-slate-900 text-white border border-slate-900">
                            {status} (custom)
                        </span>
                    )}
                </div>
                <Input
                    placeholder="Eigenen Status angeben (z.B. 'WEG-Beschluss steht aus')"
                    value={customStatus}
                    onChange={(e) => setCustomStatus(e.target.value)}
                    className="h-8 mt-2 text-xs"
                    data-testid="status-custom"
                />
            </div>

            {/* Notiz */}
            <div>
                <Label className="text-[11px] uppercase tracking-[0.18em] text-gray-500">Notiz zum Wechsel (optional)</Label>
                <Textarea
                    rows={2}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Begründung, Hintergrund, …"
                    className="text-xs mt-1.5"
                    data-testid="lifecycle-note"
                />
            </div>

            <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                <button
                    type="button"
                    onClick={() => setShowHistory((v) => !v)}
                    className="text-[11px] text-gray-500 hover:text-slate-900 inline-flex items-center gap-1"
                    data-testid="toggle-history"
                >
                    <History size={11} />
                    Historie {tracker?.history?.length ? `(${tracker.history.length})` : ""}
                </button>
                <div className="flex items-center gap-2">
                    <Button size="sm" onClick={save} disabled={busy} className="bg-slate-900 hover:bg-slate-800 h-8" data-testid="save-lifecycle">
                        {busy ? "Speichere…" : "Speichern"}
                    </Button>
                </div>
            </div>

            {showHistory && (
                <div className="border-t border-gray-100 pt-3 max-h-44 overflow-y-auto space-y-1.5">
                    {(tracker?.history || []).length === 0 && (
                        <div className="text-[11px] text-gray-400">Keine Änderungen protokolliert.</div>
                    )}
                    {[...(tracker?.history || [])].reverse().map((h) => (
                        <div key={h.id} className="flex items-start justify-between gap-2 text-[11px] text-gray-600 bg-gray-50 rounded px-2 py-1.5">
                            <div className="flex-1">
                                <div className="font-mono text-[10px] text-gray-400">{(h.at || "").slice(0, 16).replace("T", " ")}</div>
                                <div>
                                    <span className="font-medium">{fieldLabel(h.field)}:</span>{" "}
                                    <span className="text-gray-400 line-through">{h.old_value || "—"}</span>{" "}
                                    → <span className="text-slate-900">{h.new_value || "—"}</span>
                                </div>
                                {h.note && <div className="italic text-gray-500">{h.note}</div>}
                            </div>
                            <button
                                type="button"
                                onClick={() => removeHistoryEntry(h.id)}
                                className="text-gray-300 hover:text-red-600 mt-1"
                                title="Eintrag löschen"
                                data-testid={`history-delete-${h.id}`}
                            >
                                <Trash2 size={11} />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const fieldLabel = (f) => ({
    lifecycle_strategy: "Strategie",
    lifecycle_status: "Status",
    exit_target_date: "Exit-Datum",
}[f] || f);

export default LifecycleBadge;
